import {
    isValidElement,
    type ReactNode,
    useCallback,
    useState
} from "react";
import type {
    OptionRowHighlightPropsType,
    OptionRowTextConstantType,
    OptionRowHighlightsType
} from "./type";

export const getOptionRowText = ({
    node
}: OptionRowTextConstantType): string => {
    if(typeof node === "string" || typeof node === "number") {
        return String(node);
    }

    if(Array.isArray(node)) {
        return node.map((childNode: ReactNode) => getOptionRowText({
            node: childNode
        })).join("");
    }

    if(isValidElement<{ children?: ReactNode }>(node)) {
        return getOptionRowText({
            node: node.props.children
        });
    }

    return "";
};

export const useOptionRowHighlights = (): OptionRowHighlightsType => {
    const [
        highlightedKeys,
        setHighlightedKeys
    ] = useState<ReadonlySet<string>>(() => new Set<string>());

    const getHighlightProps = useCallback((key: string): OptionRowHighlightPropsType => {
        return {
            onHighlightChange: (isHighlighted: boolean) => {
                setHighlightedKeys((previousKeys) => {
                    if(previousKeys.has(key) === isHighlighted) {
                        return previousKeys;
                    }

                    const nextKeys = new Set(previousKeys);

                    if(isHighlighted) {
                        nextKeys.add(key);
                    } else {
                        nextKeys.delete(key);
                    }

                    return nextKeys;
                });
            }
        };
    }, []);

    const isHighlighted = useCallback((key?: string) => {
        return key !== undefined && highlightedKeys.has(key);
    }, [
        highlightedKeys
    ]);

    return {
        getHighlightProps,
        isHighlighted
    };
};
